import { useState } from "react";

import { Anchor } from "@/components/Anchor/Anchor";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { Modal } from "@/components/Modal/Modal";
import { Notice } from "@/components/Notice/Notice";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { Trans, useTranslation } from "@/translations";

export function CodeOfConductCommentDeletionModal({
  isOpened,
  isLoading,
  onOpenChange,
  onDelete,
}: {
  isOpened: boolean;
  isLoading?: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onDelete: () => void;
}): React.ReactNode {
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { t } = useTranslation();

  const codeOfConduct = useSessionUser().codeOfConduct.url;

  return (
    <Modal.Root title={t("component.community-post-comment.delete-modal.title")} {...{ isOpened, onOpenChange }}>
      <Notice
        type="info"
        icon={<AlertTriangleIcon />}
        message={t("component.community-post-comment.delete-modal.notice")}
      />
      <div className="flex flex-col gap-4">
        <div>
          <h4 className="text-body-bold">{t("component.community-post-comment.delete-modal.step-1")}</h4>
          <p>
            <Trans
              i18nKey="component.community-post-comment.delete-modal.step-1-description"
              components={{
                link: <Anchor isExternal inNewTab to={codeOfConduct} />,
              }}
            />
          </p>
        </div>
        <div>
          <h4 className="text-body-bold">{t("component.community-post-comment.delete-modal.step-2")}</h4>
          <p>
            <Trans
              i18nKey="component.community-post-comment.delete-modal.step-2-description"
              components={{
                link: <Anchor isExternal inNewTab to={codeOfConduct} />,
              }}
            />
          </p>
        </div>
        <div>
          <h4 className="text-body-bold">{t("component.community-post-comment.delete-modal.step-3")}</h4>
          <p>
            <Trans
              i18nKey="component.community-post-comment.delete-modal.step-3-description"
              components={{
                link: <Anchor isExternal inNewTab to={codeOfConduct} />,
              }}
            />
          </p>
        </div>
      </div>

      <Checkbox
        data-testid="checkbox-confirm-delete"
        id="checkbox-confirm"
        name="isConfirmed"
        checked={isConfirmed}
        onChange={(e) => setIsConfirmed(e.target.checked)}
        label={t("component.community-post-comment.delete-modal.confirm-checkbox")}
      />
      <Modal.Actions>
        <Button onClick={() => onOpenChange(false)} styling="secondary">
          {t("common.action.cancel")}
        </Button>
        <Button
          className="w-full sm:w-auto"
          onClick={() => onDelete()}
          styling="danger"
          disabled={!isConfirmed}
          data-testid="modal-confirm-delete"
          isLoading={isLoading}
        >
          {t("component.community-post-comment.delete-modal.confirm")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
